import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import * as taxApi from '../../lib/api/taxbill';
import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import AskDialog from '../../components/common/dialog/AskDialog';

import { CollectBuyListBox } from '../../components/collects_buys/CollectBuyListBox';
import CollectBuyStatusList from '../../components/collects_buys/CollectBuyStatusList';
import {
  listCollectsBuys,
  setCollectsBuysFilters,
} from '../../modules/taxbillModule/collects-buys';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';

const CollectBuyListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { totalPrice, collects_buys, defaultFilters, error, loading } =
    useSelector(({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.collectsBuys.totalPrice,
      collects_buys: taxbillModule.collectsBuys.collects_buys,
      defaultFilters: taxbillModule.collectsBuys.defaultFilters,
      error: taxbillModule.collectsBuys.error,
      loading: loading['taxbill/LIST_COLLECTS_BUYS'],
    }));

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      procCompYn: filters.procCompYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listCollectsBuys(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (collects_buys) {
      setTableData(collects_buys);
    }
  }, [collects_buys]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setCollectsBuysFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },
      {
        field: 'reqCompDt',
        headerName: '발행일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'salesUsrNm',
        headerName: '영업 사원',
        description: '영업 사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'osTran',
        headerName: '해외 거래',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompDt',
        headerName: '지급(수금)완료일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procCompYn',
        headerName: '지급(수금)완료여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'action',
        headerName: 'action',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => handleRemove(params.row)}
          >
            취소
          </Button>
        ),
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        searchYn: defaultFilters.searchYn,
        procCompYn: defaultFilters.procCompYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
      };
      dispatch(setCollectsBuysFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });

  //수금,지금 취소
  const [selectedItem, setSelectedItem] = useState(null);
  const [removeVisible, setRemoveVisible] = useState(false);
  const handleRemove = useCallback((row) => {
    setRemoveVisible(true);
    setSelectedItem(row);
  }, []);
  const onRemoveCancle = () => {
    setRemoveVisible(false);
    setSelectedItem(null);
  };
  const onRemoveConfirm = () => {
    //매출 취소
    onRemove();
    setRemoveVisible(false);
    setSelectedItem(null);
  };

  const onRemove = async () => {
    try {
      const response = await taxApi.onCollectBuyCancle(selectedItem?.invUuid);
      if (response.data.info) {
        dispatch(listCollectsBuys(makeFilters(defaultFilters)));
        enqueueSnackbar('취소 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <BreadcrumbsContainer
        title="채권&채무 관리/수금&지급 관리/수금&지급 리스트"
        icon="bonds_dept"
      />
      <CollectBuyListBox formik={formik} />
      <CollectBuyStatusList
        filters={defaultFilters}
        onFilters={handleFilters}
      />
      <TaxbillTotalSummary totalPrice={totalPrice} />
      <TaxbillList
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
      />
      <AskDialog
        visible={removeVisible}
        title="매출 취소"
        description={`해당 매출 "${selectedItem?.invTxt}" 을 취소하시겠습니까?`}
        onConfirm={onRemoveConfirm}
        onCancle={onRemoveCancle}
      />
    </>
  );
};

export default CollectBuyListContainer;
